/**
 * MusicCard component for Albums and Music.
 * Features a vinyl record animation that triggers when the card scrolls into view.
 */

import React, { FC } from 'react';
import { styled } from 'linaria/react';

import recordImage from '../../../assets/images/record.png';
import { useCardAnimation } from '../../../hooks/useCardAnimation';
import { FONTS } from '../../../styles/constants';
import { MusicCardProps } from '../types';
import { DIMENSIONS, BORDER_RADIUS, TYPOGRAPHY, OPACITY, MUSIC_ANIMATION } from '../styled/tokens';

const StyledMusicContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0 12px;
  overflow: visible;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
`;

const StyledContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const StyledTitleContainer = styled.div<{ isAnimating: boolean }>`
  text-align: left;
  width: 100%;
  font-family: ${FONTS.primary};
  font-size: ${TYPOGRAPHY.albumTitle.fontSize}px;
  line-height: ${TYPOGRAPHY.albumTitle.lineHeight};
  color: var(--card-text-primary, #181800);
  opacity: ${({ isAnimating }) => (isAnimating ? 1 : MUSIC_ANIMATION.entranceTitleOpacity)};
  transition: opacity ${MUSIC_ANIMATION.entranceOpacityDuration}s ${MUSIC_ANIMATION.entranceEasing};
`;

const StyledByText = styled.span`
  font-style: italic;
`;

const StyledBlurredBackground = styled.div<{ bgImage?: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${MUSIC_ANIMATION.blurInitialSize}px;
  height: ${MUSIC_ANIMATION.blurInitialSize}px;
  transform: translate(-50%, -50%);
  border-radius: ${MUSIC_ANIMATION.blurInitialRadius}px;
  z-index: 0;
  pointer-events: none;
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-size: cover;
  background-position: center;
  opacity: ${OPACITY.musicBlur};
  filter: blur(${MUSIC_ANIMATION.blurInitialFilter}px);
`;

const StyledAlbumArtContainer = styled.div<{ isAnimating: boolean }>`
  position: relative;
  overflow: visible;
  z-index: 1;
  width: ${DIMENSIONS.music.albumSize}px;
  height: ${DIMENSIONS.music.albumSize}px;
  opacity: ${({ isAnimating }) => (isAnimating ? 1 : MUSIC_ANIMATION.entranceCoverOpacity)};
  transform: ${({ isAnimating }) =>
    isAnimating
      ? 'translateY(0) scale(1)'
      : `translateY(${MUSIC_ANIMATION.entranceCoverOffsetY}px) scale(${MUSIC_ANIMATION.entranceCoverInitialScale})`};
  transition: opacity ${MUSIC_ANIMATION.entranceOpacityDuration}s ${MUSIC_ANIMATION.entranceEasing},
    transform ${MUSIC_ANIMATION.entranceScaleDuration}s ${MUSIC_ANIMATION.entranceEasing};
  will-change: opacity, transform;
`;

const StyledVinylDisc = styled.div<{ isVisible: boolean; isAnimating: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${MUSIC_ANIMATION.discSize}px;
  height: ${MUSIC_ANIMATION.discSize}px;
  margin-top: -${MUSIC_ANIMATION.discSize / 2}px;
  margin-left: -${MUSIC_ANIMATION.discSize / 2}px;
  z-index: 0;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isAnimating }) =>
    isAnimating ? 'translateX(0) scale(1)' : `translateX(0) scale(${MUSIC_ANIMATION.discInitialScale})`};
  transition: transform
    ${({ isAnimating }) => (isAnimating ? MUSIC_ANIMATION.discDuration : MUSIC_ANIMATION.exitDiscDuration)}s
    ${({ isAnimating }) => (isAnimating ? MUSIC_ANIMATION.discEasing : MUSIC_ANIMATION.exitDiscEasing)}
    ${({ isAnimating }) => (isAnimating ? `${MUSIC_ANIMATION.discDelay}s` : '0s')};
  will-change: transform;
  pointer-events: none;

  @keyframes vinylDiscSpin {
    from {
      rotate: 0deg;
    }
    to {
      rotate: 360deg;
    }
  }

  &[data-spinning='true'] {
    animation: vinylDiscSpin ${MUSIC_ANIMATION.discRotationDuration}s linear infinite;
  }
`;

const StyledVinylImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
`;

const StyledVinylCenterArt = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${MUSIC_ANIMATION.centerArtSize}px;
  height: ${MUSIC_ANIMATION.centerArtSize}px;
  margin-top: -${MUSIC_ANIMATION.centerArtSize / 2}px;
  margin-left: -${MUSIC_ANIMATION.centerArtSize / 2}px;
  border-radius: 50%;
  overflow: hidden;
`;

const StyledCenterArtImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledAlbumArt = styled.div<{ isAnimating: boolean }>`
  width: ${DIMENSIONS.music.albumSize}px;
  height: ${DIMENSIONS.music.albumSize}px;
  border-radius: ${BORDER_RADIUS.lg}px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  transform: ${({ isAnimating }) =>
    isAnimating
      ? `translateX(${MUSIC_ANIMATION.coverSlideX}px) scale(${MUSIC_ANIMATION.coverScaleEnd})`
      : 'translateX(0) scale(1)'};
  transition: transform
    ${({ isAnimating }) => (isAnimating ? MUSIC_ANIMATION.coverDuration : MUSIC_ANIMATION.exitCoverDuration)}s
    ${MUSIC_ANIMATION.coverEasing};
  will-change: transform;
`;

const StyledAlbumImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledFallback = styled.div<{ bgColor: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  font-family: ${FONTS.primary};
  font-size: 24px;
  color: var(--card-text-primary, #181800);
`;

/**
 * Format a list of creator names for display.
 *
 * 1 creator:  "Artist"
 * 2 creators: "Artist 1 & Artist 2"
 * 3+ creators: "Artist 1, Artist 2, and Artist 3" (Oxford comma)
 */
function formatCreatorNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

const MusicCard: FC<MusicCardProps> = ({
  title,
  artist,
  imageUrl,
  backgroundColor,
  externalLink,
  className,
  disableAnimation,
}) => {
  const { containerRef, isEntranceActive, isDynamicActive, isImageLoaded, onImageLoad } = useCardAnimation({
    threshold: 0.8,
    disabled: disableAnimation,
    entranceDuration: MUSIC_ANIMATION.entranceScaleDuration * 1000,
  });

  const formattedArtists = artist ? formatCreatorNames([artist]) : null;

  const musicContent = (
    <StyledContent>
      {/* Title with "by Artist(s)" format — entrance phase */}
      <StyledTitleContainer isAnimating={isEntranceActive}>
        <span>{title}</span>
        {formattedArtists && (
          <>
            {' '}
            <StyledByText>by</StyledByText> {formattedArtists}
          </>
        )}
      </StyledTitleContainer>

      {/* Album art with vinyl disc — entrance uses isEntranceActive, dynamic uses isDynamicActive */}
      <StyledAlbumArtContainer isAnimating={isEntranceActive}>
        <StyledBlurredBackground bgImage={imageUrl ?? undefined} />
        {imageUrl && (
          <StyledVinylDisc
            isVisible={isImageLoaded}
            isAnimating={isDynamicActive}
            data-spinning={isDynamicActive ? 'true' : undefined}
          >
            <StyledVinylImage src={recordImage} alt="" />
            <StyledVinylCenterArt>
              <StyledCenterArtImage src={imageUrl} alt="" />
            </StyledVinylCenterArt>
          </StyledVinylDisc>
        )}
        <StyledAlbumArt isAnimating={isDynamicActive}>
          {imageUrl ? (
            <StyledAlbumImage src={imageUrl} alt={title} loading="lazy" onLoad={onImageLoad} />
          ) : (
            <StyledFallback bgColor={backgroundColor ?? '#e8e4dc'}>{title}</StyledFallback>
          )}
        </StyledAlbumArt>
      </StyledAlbumArtContainer>
    </StyledContent>
  );

  return (
    <StyledMusicContainer className={className} ref={containerRef}>
      {externalLink ? (
        <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
          {musicContent}
        </StyledLink>
      ) : (
        musicContent
      )}
    </StyledMusicContainer>
  );
};

export default MusicCard;
